import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { Decimal } from "@/utils/decimal";
import { licensePlateNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { isDefined, numberOrZero, removeStringWhiteSpaces } from "@/utils/utils";
import { ValidateErrorEntity } from "@rc-component/form/es/interface";
import { App as AntApp, Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import {
  useCreateContract,
  useEnumeration,
  useUpdateContract,
  useValidateClient,
} from "../../../../../common/api/queries";
import { Permission } from "../../../../../common/security/enums";
import { FieldConstraintViolation, NormalizedError } from "../../../../../common/types";
import type { UUID } from "../../../../../typings/global";
import { ClientType } from "../../../../client/enums";
import { Client, NaturalClient } from "../../../../client/types";
import { clientToCreateUpdateContractClient } from "../../../../client/utils";
import { EnumerationType } from "../../../../enumerations/enums";
import { ContractType, InsuranceType } from "../../../enums";
import { useContractValidation } from "../../../hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import {
  CrashInsurance,
  CrashInsuranceData,
  CreateUpdateGenericInsurance,
  CreateUpdateInsurance,
  CreateUpdateInsuranceContract,
  CreateUpdateLifeInsurance,
  CreateUpdateRealtyInsurance,
  CreateUpdateStandardVehicleInsurance,
  CreateUpdateTravelInsurance,
  CreateUpdateVehicleInsurance,
  Insurance,
  InsuranceContract,
  InsuredClient,
  LifeInsurance,
  MtplInsurance,
  RealtyInsurance,
  StandardVehicleInsurance,
  VehicleInsurance,
} from "../../../types";
import { parseClientsViolationErrors } from "../../../utils";
import ContractFormAgentsSection from "../ContractFormAgentsSection";
import InsuranceContractFormDataSection from "./sections/InsuranceContractFormDataSection";
import { InsuranceContractFormHeaderSection } from "./sections/InsuranceContractFormHeaderSection";
import { InsuranceContractFormInsurancesSection } from "./sections/InsuranceContractFormInsurancesSection";

type Props = {
  initialContract?: InsuranceContract;
  onCancel?: () => void;
};

// Client selects on vehicle insurances work with identifiers, the API takes indexes into contract clients
type VehicleInsuranceIdentifiers = {
  vehicleHolderIdentifier?: string;
  vehicleOwnerIdentifier?: string;
};

const sumInsurancesPremiums = (
  premium: "annualPremium" | "partialPremium",
  insurances?: { annualPremium?: number; partialPremium?: number }[],
) => (insurances ?? []).reduce((sum, insurance) => Decimal.sum(sum, numberOrZero(insurance[premium])), 0);

export const InsuranceContractForm = ({ initialContract, onCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const navigate = useNavigate();

  const productEnums = useEnumeration(EnumerationType.PRODUCTS);
  const clientValidation = useValidateClient();

  const [clients, setClients] = useState<(Partial<Client> | undefined)[]>(initialContract?.clients || []);
  const [clientsCount, setClientsCount] = useState<number>(initialContract?.clients.length || 1);
  const [clientsViolationErrors, setClientsViolationErrors] = useState<Map<number, FieldConstraintViolation[]>>(
    () => new Map(),
  );
  const [insurancesCount, setInsurancesCount] = useState<number>(initialContract?.insurances.length || 1);

  const [insurancesAnnualPremiumsSum, setInsurancesAnnualPremiumsSum] = useState<number>(() =>
    sumInsurancesPremiums("annualPremium", initialContract?.insurances),
  );
  const [insurancesPartialPremiumsSum, setInsurancesPartialPremiumsSum] = useState<number>(() =>
    sumInsurancesPremiums("partialPremium", initialContract?.insurances),
  );

  const [form] = Form.useForm<CreateUpdateInsuranceContract>();

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const handleContractValidationChange = useContractValidation({ form, contractId: initialContract?.id });

  useEffect(() => {
    if (initialContract) {
      const initialContractCopy = structuredClone(initialContract);
      const formData = {
        ...initialContractCopy,
        historyRecords: undefined,
        clients: undefined,
        clientIds: undefined,
        institution: undefined,
        product: undefined,
        signer: undefined,
        manager: undefined,
        affiliatePartner: undefined,
        attachments: undefined,
        institutionId: initialContractCopy.institution.id,
        productGroupId: productEnums.find((product) => product.id === initialContractCopy.product.id)?.productGroupId,
        productId: initialContractCopy.product.id,
        clientIdentifiers: initialContractCopy.clients.map((client) => client.identifier),
        signerId: initialContractCopy.signer?.id,
        managerId: initialContractCopy.manager?.id,
        affiliatePartnerId: initialContractCopy.affiliatePartner?.id,
        gainerRecords: initialContractCopy.gainerRecords?.map((record) => ({
          id: record.id,
          optimisticLockVersion: record.optimisticLockVersion,
          startDate: record.startDate,
          endDate: record.endDate,
          gainer1Id: record.gainer1.id,
          gainer1Rate: record.gainer1Rate,
          gainer2Id: record.gainer2?.id,
          gainer2Rate: record.gainer2Rate,
          gainer3Id: record.gainer3?.id,
          gainer3Rate: record.gainer3Rate,
          gainer4Id: record.gainer4?.id,
          gainer4Rate: record.gainer4Rate,
          gainer5Id: record.gainer5?.id,
          gainer5Rate: record.gainer5Rate,
        })),
        insurances: (initialContractCopy.insurances as Insurance[]).map((initialInsurance) => {
          const insurance = structuredClone(initialInsurance);

          if ("insuredClientIndex" in initialInsurance) {
            (
              insurance as CreateUpdateGenericInsurance | CreateUpdateRealtyInsurance | CreateUpdateLifeInsurance
            ).insuredClientIdentifier = initialContractCopy.clients[initialInsurance.insuredClientIndex]?.identifier;
          }

          switch (insurance.type) {
            case InsuranceType.MTPL:
            case InsuranceType.CRASH:
            case InsuranceType.GAP:
            case InsuranceType.PAS:
              const vehicleInsurance = { ...insurance } as Partial<CreateUpdateStandardVehicleInsurance> &
                StandardVehicleInsurance;
              // @ts-ignore ts-problem
              delete vehicleInsurance["coverageLimit"];
              // @ts-ignore ts-problem
              delete vehicleInsurance["complicity"];
              // @ts-ignore ts-problem
              delete vehicleInsurance.vehicle["model"];
              // @ts-ignore ts-problem
              delete vehicleInsurance.vehicle["color"];
              // @ts-ignore ts-problem
              delete vehicleInsurance.vehicle["certificateCategory"];

              const resultVehicleInsurance: CreateUpdateStandardVehicleInsurance = {
                ...vehicleInsurance,
                licensePlate: licensePlateNormalizeFunction(vehicleInsurance.licensePlate),
                // CRASH may come without a coverage limit, but there the target field is optional too;
                // for MTPL the backend guarantees it (@NotNull), which TS cannot narrow through this union
                coverageLimitId: (initialInsurance as MtplInsurance | CrashInsurance).coverageLimit?.id as UUID,
                // @ts-ignore ts-problem
                complicityId: (initialInsurance as CrashInsurance).complicity?.id,
                vehicleOwnerIdentifier:
                  initialContractCopy.clients[(initialInsurance as StandardVehicleInsurance)["vehicleOwnerIndex"]]
                    ?.identifier,
                vehicleHolderIdentifier:
                  initialContractCopy.clients[(initialInsurance as StandardVehicleInsurance)["vehicleHolderIndex"]]
                    ?.identifier,
              };

              if (vehicleInsurance.type === InsuranceType.CRASH) {
                const crashInsuranceData = vehicleInsurance.insuranceData as CrashInsuranceData;
                crashInsuranceData.coverages.gapComplicityReinsurance =
                  !!crashInsuranceData.coverages.gapComplicityReinsurance;
              }

              const initialVehicle = (initialInsurance as StandardVehicleInsurance).vehicle;

              resultVehicleInsurance.vehicle = {
                ...resultVehicleInsurance.vehicle,
                firstRegistrationDate: initialVehicle.firstRegistrationDate,
                colorId: initialVehicle.color.id,
                brandId: initialVehicle.model.brand.id,
                modelId: initialVehicle.model.id,
              };

              return resultVehicleInsurance;
            case InsuranceType.VEHICLE:
              const otherVehicleInsurance = { ...insurance } as Partial<CreateUpdateVehicleInsurance> &
                VehicleInsurance;
              const linkedVehicle = otherVehicleInsurance.vehicle;
              // This type carries the vehicle only as a read-only link — the form edits it through vehicleId
              delete otherVehicleInsurance["vehicle"];

              return {
                ...otherVehicleInsurance,
                licensePlate: licensePlateNormalizeFunction(otherVehicleInsurance.licensePlate),
                vehicleId: linkedVehicle?.id,
                vehicleOwnerIdentifier:
                  initialContractCopy.clients[(initialInsurance as VehicleInsurance)["vehicleOwnerIndex"]]?.identifier,
                vehicleHolderIdentifier:
                  initialContractCopy.clients[(initialInsurance as VehicleInsurance)["vehicleHolderIndex"]]?.identifier,
              } as CreateUpdateInsurance;
            case InsuranceType.REALTY:
              const realtyInsurance = insurance as CreateUpdateRealtyInsurance;
              const vinculationClientIndex = (initialInsurance as RealtyInsurance).vinculationClientIndex;

              realtyInsurance.insuranceData.realtyEnabled = !!realtyInsurance.insuranceData.realty;
              realtyInsurance.insuranceData.householdEnabled = !!realtyInsurance.insuranceData.household;
              realtyInsurance.vinculationClientIdentifier = isDefined(vinculationClientIndex)
                ? initialContractCopy.clients[vinculationClientIndex]?.identifier
                : undefined;
              return realtyInsurance;
            case InsuranceType.TRAVEL:
              const travelInsurance = insurance as CreateUpdateTravelInsurance;
              travelInsurance.insuranceData.insuredClients = travelInsurance.insuranceData.insuredClients.map(
                (client) => ({
                  ...client,
                }),
              );
              return travelInsurance;
            case InsuranceType.LIFE:
              const lifeInsurance = { ...insurance } as CreateUpdateLifeInsurance & LifeInsurance;
              lifeInsurance.tariffId = (initialInsurance as LifeInsurance).tariff.id;
              // @ts-ignore ts-problem
              delete lifeInsurance["tariff"];
              return lifeInsurance as CreateUpdateInsurance;
            default:
              return insurance;
          }
        }),
      } as Record<string, any>;

      if (formData.insurances.length === 1) {
        formData.insurances[0] = {
          ...formData.insurances[0],
          contractEntryDate: undefined,
          contractWithdrawalDate: undefined,
          annualPremium: undefined,
          partialPremium: undefined,
        };
      }

      form.setFieldsValue(formData);
    }
  }, []);

  const handleSaveError = (error: NormalizedError) => {
    setErrorsToForm(form, "contract.attrs", error.violations);
    if (error.violations) {
      setClientsViolationErrors(parseClientsViolationErrors(error.violations));
    }
  };

  const handleFormFinish = () => {
    form
      .validateFields()
      .then((values) => {
        const usedIdentifiers = new Set<string>();
        usedIdentifiers.add(clients[0]?.identifier as string);
        (values.insurances as CreateUpdateInsurance[]).forEach((insurance) => {
          const insuredClientIdentifier = (
            insurance as CreateUpdateGenericInsurance | CreateUpdateRealtyInsurance | CreateUpdateLifeInsurance
          ).insuredClientIdentifier;

          if (insuredClientIdentifier) {
            usedIdentifiers.add(insuredClientIdentifier);
          }

          const { vehicleHolderIdentifier, vehicleOwnerIdentifier } = insurance as VehicleInsuranceIdentifiers;

          if (vehicleHolderIdentifier) {
            usedIdentifiers.add(vehicleHolderIdentifier);
          }

          if (vehicleOwnerIdentifier) {
            usedIdentifiers.add(vehicleOwnerIdentifier);
          }

          const vinculationClientIdentifier = (insurance as CreateUpdateRealtyInsurance)["vinculationClientIdentifier"];
          if (vinculationClientIdentifier) {
            usedIdentifiers.add(vinculationClientIdentifier);
          }
        });

        const unusedClients = clients.filter((client) => client?.identifier && !usedIdentifiers.has(client.identifier));

        if (clientsViolationErrors.size > 0 || clients.filter((client) => !!client).length !== clientsCount) {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formError"),
          });
        } else if (unusedClients.length > 0) {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formErrorUnusedClient", {
              clientIdentifiers: unusedClients.flatMap((c) => c?.identifier).join(", "),
            }),
          });
        } else {
          const processedValues = { ...values };

          processedValues.insurances = processedValues.insurances.map((insuranceData) => {
            const insurance = { ...insuranceData } as CreateUpdateInsurance;

            if ("insuredClientIdentifier" in insurance) {
              insurance.insuredClientIndex = findClientIndex(insurance.insuredClientIdentifier);
              delete insurance.insuredClientIdentifier;
            }

            switch (insurance.type) {
              case InsuranceType.MTPL:
              case InsuranceType.CRASH:
              case InsuranceType.GAP:
              case InsuranceType.PAS:
                const vehicleInsurance = insurance as CreateUpdateStandardVehicleInsurance &
                  VehicleInsuranceIdentifiers;

                vehicleInsurance.vehicleHolderIndex = findClientIndex(vehicleInsurance.vehicleHolderIdentifier);
                vehicleInsurance.vehicleOwnerIndex = findClientIndex(vehicleInsurance.vehicleOwnerIdentifier);
                delete vehicleInsurance.vehicleHolderIdentifier;
                delete vehicleInsurance.vehicleOwnerIdentifier;

                vehicleInsurance.licensePlate = removeStringWhiteSpaces(vehicleInsurance.licensePlate);
                vehicleInsurance.vehicle.licensePlate = vehicleInsurance.licensePlate;
                vehicleInsurance.vehicle.registrationCertificateNumber =
                  vehicleInsurance.insuranceData.registrationCertificateNumber || "";
                delete vehicleInsurance.vehicle.brandId;

                return vehicleInsurance;
              case InsuranceType.VEHICLE:
                const otherVehicleInsurance = insurance as CreateUpdateVehicleInsurance & VehicleInsuranceIdentifiers;

                otherVehicleInsurance.vehicleHolderIndex = findClientIndex(
                  otherVehicleInsurance.vehicleHolderIdentifier,
                );
                otherVehicleInsurance.vehicleOwnerIndex = findClientIndex(otherVehicleInsurance.vehicleOwnerIdentifier);
                delete otherVehicleInsurance.vehicleHolderIdentifier;
                delete otherVehicleInsurance.vehicleOwnerIdentifier;

                otherVehicleInsurance.licensePlate = removeStringWhiteSpaces(otherVehicleInsurance.licensePlate);

                return otherVehicleInsurance;
              case InsuranceType.REALTY:
                const realtyInsurance = insurance as CreateUpdateRealtyInsurance;

                realtyInsurance.vinculationClientIndex = findClientIndex(realtyInsurance.vinculationClientIdentifier);
                delete realtyInsurance.vinculationClientIdentifier;
                delete realtyInsurance.insuranceData.realtyEnabled;
                delete realtyInsurance.insuranceData.householdEnabled;

                return realtyInsurance;
              default:
                return insurance;
            }
          });

          if (processedValues.insurances.length === 1) {
            processedValues.insurances[0] = {
              ...(processedValues.insurances[0] as CreateUpdateInsurance),
              annualPremium: processedValues.annualPremium,
              partialPremium: processedValues.partialPremium,
              contractEntryDate: processedValues.effectiveBeginningDate,
            };
          }

          processedValues.clients = clients
            .filter((client): client is Client => !!client)
            .map((client) => clientToCreateUpdateContractClient(client));
          processedValues.clientIds = [];
          processedValues.policyHolderIndex = 0;

          delete processedValues.productGroupId;
          delete processedValues.clientIdentifiers;

          if (initialContract) {
            updateContract.mutate(
              {
                id: initialContract.id,
                object: {
                  ...processedValues,
                  paidUntilDate: initialContract.paidUntilDate,
                  amountOwed: initialContract.amountOwed,
                } as CreateUpdateInsuranceContract,
              },
              {
                onSuccess: onCancel,
                onError: handleSaveError,
              },
            );
          } else {
            createContract.mutate(processedValues, {
              onError: handleSaveError,
              onSuccess: (data) => {
                navigate(generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: data.id }));
              },
            });
          }
        }
      })
      .catch((errors: ValidateErrorEntity) => {
        const errorInsurancesNumbers = [
          ...new Set(
            errors.errorFields
              .filter((field) => field.name[0] === "insurances" && typeof field.name[1] === "number")
              .map((field) => (field.name[1] as number) + 1),
          ),
        ];

        if (errorInsurancesNumbers.length > 0) {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formErrorWithInsurances", {
              insurancesNumbers: errorInsurancesNumbers.join(", "),
            }),
          });
        } else {
          notification.error({
            title: t("common.error"),
            description: t("contract.validations.formError"),
          });
        }

        resolveFormValidationError(errors);
      });
  };

  const handleClientChange = (index: number, client?: Partial<Client>) => {
    setClients((prevState) => {
      const updatedClients = [...prevState];
      updatedClients[index] = client;

      return updatedClients;
    });

    if (client) {
      clientValidation.mutate(
        { prefix: `clients[${index}]`, client: clientToCreateUpdateContractClient(client) },
        {
          onError: (error) => {
            if (error.violations) {
              const violations = error.violations;
              const indexString = violations[0]?.fieldPath.charAt(8);

              if (!indexString) {
                return;
              }

              const index = parseInt(indexString);
              const updatedViolationsErrors = new Map<number, FieldConstraintViolation[]>([...clientsViolationErrors]);
              updatedViolationsErrors.set(
                index,
                violations.map((v) => ({ ...v, fieldPath: v.fieldPath.substring(18) })),
              );
              setClientsViolationErrors(updatedViolationsErrors);
            }
          },
        },
      );
    }

    if (index === 0) {
      const updatedInsurances = [...(form.getFieldValue(["insurances"]) || [])] as CreateUpdateInsurance[];
      form.setFieldsValue({
        insurances: updatedInsurances.map((updatedInsurance) => {
          if (updatedInsurance.type === InsuranceType.TRAVEL) {
            const travel = { ...updatedInsurance } as CreateUpdateTravelInsurance;
            if (travel.insuranceData.policyHolderIsAlsoInsured) {
              const insuredClients: Record<string, any> = [...(travel.insuranceData.insuredClients || [])];
              if (client?.type === ClientType.NATURAL) {
                insuredClients[0] = {
                  firstName: (client as NaturalClient).firstName,
                  lastName: (client as NaturalClient).lastName,
                  birthDate: (client as NaturalClient).birthDate,
                };
                travel.insuranceData = { ...travel.insuranceData, insuredClients: insuredClients as InsuredClient[] };
              } else {
                insuredClients[0] = {
                  firstName: undefined,
                  lastName: undefined,
                  birthDate: undefined,
                };
                travel.insuranceData = {
                  ...travel.insuranceData,
                  policyHolderIsAlsoInsured: false,
                  insuredClients: insuredClients as InsuredClient[],
                };
              }
            }
            return travel;
          }
          return updatedInsurance;
        }),
      });
    }
  };

  const handleClientDelete = (index: number) => {
    setClients((prevState) => {
      const updatedClients = [...prevState];
      updatedClients.splice(index, 1);

      return updatedClients;
    });
  };

  const handleInsuranceAnnualPremiumChange = () => {
    setInsurancesAnnualPremiumsSum(sumInsurancesPremiums("annualPremium", form.getFieldValue(["insurances"])));
  };

  const handleInsurancePartialPremiumChange = () => {
    setInsurancesPartialPremiumsSum(sumInsurancesPremiums("partialPremium", form.getFieldValue(["insurances"])));
  };

  const findClientIndex = (identifier?: string) => {
    if (identifier) {
      const index = clients.findIndex((client) => client?.identifier === identifier);
      return index === -1 ? undefined : index;
    }
    return undefined;
  };

  return (
    <Form form={form} layout="vertical" name="insuranceContractForm" onValuesChange={handleContractValidationChange}>
      <HiddenInput name="type" initialValue={ContractType.INSURANCE_CONTRACT} />
      <HiddenInput name="optimisticLockVersion" />
      <HiddenInput name="status" />

      <InsuranceContractFormHeaderSection
        initialContract={initialContract}
        clients={clients}
        clientsCount={clientsCount}
        setClientsCount={setClientsCount}
        clientsViolationErrors={clientsViolationErrors}
        insurancesCount={insurancesCount}
        onClientChange={handleClientChange}
        onClientDelete={handleClientDelete}
        onClientViolationErrorsChange={setClientsViolationErrors}
      />

      <InsuranceContractFormDataSection
        insurancesCount={insurancesCount}
        insurancesAnnualPremiumsSum={insurancesAnnualPremiumsSum}
        insurancesPartialPremiumsSum={insurancesPartialPremiumsSum}
      />

      <InsuranceContractFormInsurancesSection
        initialContract={initialContract}
        clients={clients}
        insurancesCount={insurancesCount}
        onInsurancesCountChange={setInsurancesCount}
        onAnnualPremiumChange={handleInsuranceAnnualPremiumChange}
        onPartialPremiumChange={handleInsurancePartialPremiumChange}
      />

      <ContractFormAgentsSection
        initialContract={initialContract}
        privilegedChangesPermission={Permission.PRIVILEGED_CHANGES_ON_VERIFIED_INSURANCE}
      />

      <div className="margin-top-small">
        <Button type="primary" icon={<AntIcon type="save" />} onClick={handleFormFinish}>
          {t("common.save")}
        </Button>

        {onCancel && (
          <Button className="margin-left-tiny" onClick={onCancel} icon={<AntIcon type="close" />}>
            {t("common.cancel")}
          </Button>
        )}
      </div>
    </Form>
  );
};
